import { useEffect, useState } from "react";
import { Save, Mail, AlertCircle } from "lucide-react";
import api, { formatErr } from "../lib/apiClient";

const TEMPLATE_KEYS = [
  { key: "invoice", label: "Invoice" },
  { key: "estimate", label: "Estimate" },
  { key: "reminder", label: "Reminder" },
  { key: "thankyou", label: "Thank You" },
  { key: "invite", label: "Employee Invite" },
];

export default function EmailSettings() {
  const [cfg, setCfg] = useState({ resend_api_key: "", email_from: "", notification_bcc_email: "", reminder_days: [3, 7, 14], ontario_corp_tax_rate: 12.2 });
  const [status, setStatus] = useState({ configured: false, has_api_key: false });
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");

  const load = async () => {
    const [{ data: s }, { data: t }] = await Promise.all([api.get("/settings/email"), api.get("/email-templates")]);
    setStatus(s);
    setCfg((c) => ({ ...c, email_from: s.email_from, notification_bcc_email: s.notification_bcc_email || "", reminder_days: s.reminder_days, ontario_corp_tax_rate: s.ontario_corp_tax_rate }));
    setTemplates(t);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setErr(""); setMsg("");
    try {
      const payload = {
        email_from: cfg.email_from || null,
        notification_bcc_email: (cfg.notification_bcc_email || "").trim() || null,
        reminder_days: (cfg.reminder_days || []).map(Number).filter((n) => Number.isFinite(n)),
        ontario_corp_tax_rate: Number(cfg.ontario_corp_tax_rate),
      };
      if (cfg.resend_api_key) payload.resend_api_key = cfg.resend_api_key;
      await api.put("/settings/email", payload);
      setMsg("Email settings saved");
      setCfg((c) => ({ ...c, resend_api_key: "" }));
      load();
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { setErr(formatErr(e)); }
  };

  const saveTemplate = async () => {
    try {
      await api.put(`/email-templates/${editing.key}`, {
        key: editing.key, name: editing.name || editing.key,
        subject: editing.subject, body_html: editing.body_html,
      });
      setMsg("Template saved");
      setEditing(null);
      load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const resetTemplate = async (key) => {
    if (!window.confirm("Reset to default template?")) return;
    await api.post(`/email-templates/${key}/reset`);
    if (editing && editing.key === key) setEditing(null);
    load();
  };

  return (
    <div data-testid="email-settings-page" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Email &amp; Tax Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Resend email integration, late-payment reminders &amp; corp tax rate.</p>
      </div>
      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-900 px-3 py-2 rounded">{msg}</div>}
      {err && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950 px-3 py-2 rounded">{err}</div>}

      {!status.has_api_key && (
        <div data-testid="resend-not-configured" className="border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950 rounded p-4 flex items-start gap-3">
          <AlertCircle className="text-amber-600 mt-0.5" size={18} />
          <div className="text-sm">
            <div className="font-semibold text-amber-900 dark:text-amber-200">Resend API key not set</div>
            <div className="text-amber-800 dark:text-amber-300">Emails are being logged but not delivered. Add your key below to start sending. Get a free key at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a>.</div>
          </div>
        </div>
      )}

      <form onSubmit={save} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6 space-y-4">
        <h3 className="font-display font-semibold flex items-center gap-2"><Mail size={18} /> Resend Integration</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">Resend API Key {status.has_api_key && <span className="text-emerald-600 ml-2">(currently set)</span>}</label>
            <input data-testid="resend-key-input" value={cfg.resend_api_key} onChange={(e) => setCfg({ ...cfg, resend_api_key: e.target.value })} placeholder={status.has_api_key ? "•••••••• (paste to replace)" : "re_xxxxxxxxxxxx"} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md font-mono" />
          </div>
          <div><label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">From Address</label>
            <input data-testid="email-from" value={cfg.email_from || ""} onChange={(e) => setCfg({ ...cfg, email_from: e.target.value })} placeholder="onboarding@resend.dev" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md" />
            <p className="text-xs text-slate-500 mt-1">Must be a verified domain on Resend (or use their sandbox).</p>
          </div>
          <div><label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">BCC every invoice to</label>
            <input data-testid="notification-bcc-email" type="email" value={cfg.notification_bcc_email || ""} onChange={(e) => setCfg({ ...cfg, notification_bcc_email: e.target.value })} placeholder="info@yourcompany.ca" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md" />
            <p className="text-xs text-slate-500 mt-1">Every invoice email (send + reminder) is BCC'd here for your records. Leave blank to disable.</p>
          </div>
          <div><label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">Reminder Days (comma-separated)</label>
            <input data-testid="reminder-days" value={(cfg.reminder_days || []).join(",")} onChange={(e) => setCfg({ ...cfg, reminder_days: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md tabular" />
            <p className="text-xs text-slate-500 mt-1">e.g. 3,7,14 — automatic reminders X days overdue</p>
          </div>
          <div><label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">Ontario Corp Tax Rate (%)</label>
            <input data-testid="corp-tax-rate" type="number" step="0.1" value={cfg.ontario_corp_tax_rate} onChange={(e) => setCfg({ ...cfg, ontario_corp_tax_rate: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md tabular" />
            <p className="text-xs text-slate-500 mt-1">12.2% = CCPC small business · 26.5% = general</p>
          </div>
        </div>
        <button type="submit" data-testid="save-email-settings" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Save size={14} /> Save</button>
      </form>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
        <h3 className="font-display font-semibold mb-3">Email Templates</h3>
        <p className="text-xs text-slate-500 mb-4">Variables: <code>{`{{customer_name}}`}</code> <code>{`{{number}}`}</code> <code>{`{{total}}`}</code> <code>{`{{due_date}}`}</code> <code>{`{{business_name}}`}</code> <code>{`{{days_overdue}}`}</code></p>
        <div className="space-y-2">
          {TEMPLATE_KEYS.map((tk) => {
            const t = templates.find((x) => x.key === tk.key) || {};
            const isCustom = !t.is_default;
            return (
              <div key={tk.key} data-testid={`tpl-row-${tk.key}`} className="border border-slate-200 dark:border-slate-700 rounded p-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-sm flex items-center gap-2">{tk.label}{isCustom && <span className="text-xs px-1.5 py-0.5 bg-[#E0F2FE] text-[#0F4C81] rounded">customized</span>}</div>
                    <div className="text-xs text-slate-500 mt-1">{t.subject}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ ...t, key: tk.key, name: tk.label })} className="text-xs text-[#0F4C81] hover:underline">Edit</button>
                    {isCustom && <button onClick={() => resetTemplate(tk.key)} className="text-xs text-slate-500 hover:underline">Reset</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-md max-w-2xl w-full p-6 space-y-3 max-h-[90vh] overflow-auto">
            <div className="flex justify-between"><h3 className="font-display text-xl font-bold">Edit Template — {editing.name}</h3><button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-900">✕</button></div>
            <div><label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">Subject</label>
              <input value={editing.subject || ""} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md" />
            </div>
            <div><label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">Body (HTML)</label>
              <textarea value={editing.body_html || ""} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} rows={10} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md font-mono text-xs" />
            </div>
            <div className="flex gap-2">
              <button onClick={saveTemplate} className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Save Template</button>
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-slate-300 dark:border-slate-700 text-sm rounded-md hover:bg-slate-50 dark:hover:bg-slate-800">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
